/**
 * Ship size bands (M10 P1d, hull tiers spec §2.1): `src/data/bands.json`, Zod-validated here. The band comes
 * from the asset's bounds length unless `ships.json band` overrides it (`ShipDef.sizeBand`, `shipResolve.ts`).
 */
import { z } from 'zod'
import bandsJson from './bands.json'
import { flightPresetSchema, SHIP_BAND_IDS, shipBandSchema, type FlightPreset, type ShipBand } from './schemas'

export const DOCK_MODES = ['bay', 'moor', 'station'] as const
export type DockMode = (typeof DOCK_MODES)[number]

const bandRowSchema = z.strictObject({
  /** The longest bounds length (m, max.z − min.z) in this band. */
  maxLengthM: z.number().positive(),
  dockMode: z.enum(DOCK_MODES),
  /** Count against `maxRealShips`. */
  localWeight: z.number().int().positive(),
  /** The longest flight-assist stop from top speed (s) a hull of this band may take (D-249); none for XL yet. */
  faStopMaxS: z.number().positive().optional(),
  /**
   * The cruise spin-up lag τ (s) for a hull of this band (M10 P5a7a); absent: `reach-travel.json cruise.lagS`. The
   * shipped L sits at today's global until the W6 flip (review F5), so every hull cruises as before.
   */
  cruiseLagS: z.number().nonnegative().optional(),
  /** The hold time (s) to engage the drive for a hull of this band (M10 P5a7a); absent: `cruise.chargeS`. */
  cruiseChargeS: z.number().positive().optional(),
})

export const bandsConfigSchema = z
  .strictObject({
    comment: z.string().optional(),
    bands: z.strictObject({ S: bandRowSchema, M: bandRowSchema, L: bandRowSchema, XL: bandRowSchema }),
    /** Bay socket size → the bands that bay takes. */
    bayAccepts: z.partialRecord(shipBandSchema, z.array(shipBandSchema).min(1)),
    flightPresets: z.partialRecord(shipBandSchema, flightPresetSchema),
  })
  .refine((c) => SHIP_BAND_IDS.every((b, i) => i === 0 || c.bands[b].maxLengthM > c.bands[SHIP_BAND_IDS[i - 1]].maxLengthM), {
    message: 'band maxLengthM must rise S < M < L < XL',
  })
  .refine((c) => Object.entries(c.bayAccepts).every(([bay, bands]) => bands?.includes(bay as ShipBand) ?? true), {
    message: 'a bay must take its own band',
  })

export type BandsConfig = z.infer<typeof bandsConfigSchema>

/** Parses and validates bands.json (throws a ZodError naming the bad field). */
export function loadBandsConfig(raw: unknown = bandsJson): BandsConfig {
  return bandsConfigSchema.parse(raw)
}

let cached: BandsConfig | undefined
function config(): BandsConfig {
  cached ??= loadBandsConfig()
  return cached
}

/** The band whose `maxLengthM` first reaches `lengthM`. Throws past the XL limit. */
export function bandOfLength(lengthM: number, cfg: BandsConfig = config()): ShipBand {
  for (const b of SHIP_BAND_IDS) if (lengthM <= cfg.bands[b].maxLengthM) return b
  throw new RangeError(`bands.json: length ${lengthM} m is past the XL limit of ${cfg.bands.XL.maxLengthM} m`)
}

/** 0 for S up to 3 for XL. */
export function bandRank(band: ShipBand): number {
  return SHIP_BAND_IDS.indexOf(band)
}

export function dockModeOf(band: ShipBand, cfg: BandsConfig = config()): DockMode {
  return cfg.bands[band].dockMode
}

/** Whether a bay of socket size `baySize` takes a ship of `band`. An unknown bay size takes nothing. */
export function bayAccepts(baySize: string, band: ShipBand, cfg: BandsConfig = config()): boolean {
  const parsed = shipBandSchema.safeParse(baySize)
  if (!parsed.success) return false
  return cfg.bayAccepts[parsed.data]?.includes(band) ?? false
}

export function localWeightOf(band: ShipBand, cfg: BandsConfig = config()): number {
  return cfg.bands[band].localWeight
}

/**
 * The bands an AI may fly (plan 2026-10-10-m10-p1 §0.2 item 6: no AI flies an L until P4b). It gates the dev spawn
 * menu only. One attributed exception (M10 P3c1, plan 2026-10-11-m10-p3 question 4, D-771): `hauler_large` is band L
 * on its 190 m block and is flown by mission AI (the Mission 2 lessee barge) and fleet AI captains from P3c1, on the
 * P1e3 ring path; no AI mix, war band, escort or spawn list names an L before P4b5 (`aiBands.guard.test.ts`).
 *
 * The flagship exception (M10 P4b5b, plan 2026-10-15-m10-p4b §0.3): from P4b5 the strategic AI orders capped L
 * flagships of its own maker's lines through hull-lines `buyerMixes` and `flagship.cap` (`agents/jobs.ts
 * flagshipAllowed`; all 0 until the P4b8 flips), and those L ships fly as AI when promoted. This list does not gate
 * them: it gates only the dev spawn menu. `roles.json`, the war band, escorts, encounters and waves still name no L,
 * and outlaws never order one (`aiBands.guard.test.ts`, the flagship guard).
 */
export const AI_BANDS: readonly ShipBand[] = ['S', 'M']

/** Whether an AI may fly a hull of `band`. */
export function aiFliesBand(band: ShipBand): boolean {
  return AI_BANDS.includes(band)
}

/** The band's flight-assist stop bound in seconds, or undefined when bands.json sets none. */
export function faStopMaxOf(band: ShipBand, cfg: BandsConfig = config()): number | undefined {
  return cfg.bands[band].faStopMaxS
}

/** The `flightBand` preset, or undefined when bands.json has none for that band. */
export function flightPresetOf(band: ShipBand, cfg: BandsConfig = config()): FlightPreset | undefined {
  return cfg.flightPresets[band]
}

/** The band's cruise spin-up lag (s), or undefined when bands.json sets none (the global `cruise.lagS` applies). */
export function cruiseLagSOf(band: ShipBand, cfg: BandsConfig = config()): number | undefined {
  return cfg.bands[band].cruiseLagS
}

/** The band's cruise charge hold (s), or undefined when bands.json sets none (the global `cruise.chargeS` applies). */
export function cruiseChargeSOf(band: ShipBand, cfg: BandsConfig = config()): number | undefined {
  return cfg.bands[band].cruiseChargeS
}
